import { Typography, Box, useTheme, useMediaQuery } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo, useRef, useState, useEffect } from "react";

export default function EscapeArtistsChart({
    data = [],
    rowHeight = 22,
    maxChartHeight = 700,
    minChartHeight = 700,
    labelMaxChars = 18,
    tittle,
    activeTitle = true,
    activeIsolates = true,
    customIsolates = { active: false, value: 0 }
}) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const [zoom, setZoom] = useState(() =>
        data.length < 20 ? { start: 0, end: 100 } : { start: 0, end: 30 }
    );

    useEffect(() => {
        if (data.length < 20) {
            setZoom({ start: 0, end: 100 });
        } else {
            setZoom({ start: 0, end: 30 });
        }
    }, [data]);

    const chartRef = useRef(null);

    const { categories, values, chartHeight, isolates } = useMemo(() => {
        const sorted = [...data].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

        const categories = sorted.map((d) => d.name || d.gene || "—");
        const values = sorted.map((d) => Number(d.value) || 0);

        const isolates = sorted.reduce((acc, item) => {
            return acc + (Number(item?.value) || 0);
        }, 0);

        const ideal = categories.length * rowHeight + 220;
        const chartHeight = Math.max(minChartHeight, Math.min(maxChartHeight, ideal));

        return { categories, values, chartHeight, isolates };
    }, [data, rowHeight, maxChartHeight, minChartHeight]);

    const total = categories.length || 1;

    const visibleCount = useMemo(() => {
        const startIdx = Math.floor((zoom.start / 100) * total);
        const endIdx = Math.ceil((zoom.end / 100) * total);
        return Math.max(0, endIdx - startIdx);
    }, [zoom, total]);

    const seriesData = useMemo(() => {
        const base = { r: 32, g: 188, b: 113 };

        return values.map((v) => {
            const t = Math.random() * 0.55;
            const r = Math.round(base.r + (255 - base.r) * t);
            const g = Math.round(base.g + (255 - base.g) * t);
            const b = Math.round(base.b + (255 - base.b) * t);

            return {
                value: v,
                itemStyle: {
                    color: `rgb(${r}, ${g}, ${b})`,
                    borderRadius: [0, 10, 10, 0],
                },
            };
        });
    }, [values]);

    const labelStep = useMemo(() => {
        if (visibleCount <= 30) return 1;
        if (visibleCount <= 60) return 2;
        if (visibleCount <= 120) return 4;
        return 8;
    }, [visibleCount]);

    const option = useMemo(
        () => ({
            animation: false,
            grid: {
                left: isSmallScreen ? 10 : 50,
                right: isSmallScreen ? 10 : 20,
                top: 50,
                bottom: 55,
                containLabel: true,
            },

            tooltip: {
                trigger: "item",
                formatter: (p) => {
                    if (!p) return "";
                    const name = p.name ?? "";
                    const val =
                        typeof p.value === "number"
                            ? p.value
                            : typeof p?.data?.value === "number"
                                ? p.data.value
                                : p?.data ?? "";
                    return `${name}<br/><b>${val}</b>`;
                },
            },

            xAxis: {
                type: "value",
                min: 0,
                axisLabel: { fontSize: isSmallScreen ? 10 : 12 },
                position: "top",
                splitLine: { show: true, lineStyle: { type: "dashed", color: "#222" } },
            },

            yAxis: {
                type: "category",
                data: categories,
                inverse: true,
                axisLabel: {
                    fontSize: isSmallScreen ? 11 : 13,
                    interval: 0,
                    formatter: (val, idx) => {
                        if (labelStep > 1 && idx % labelStep !== 0) return "";
                        const s = String(val);
                        const maxChars = isSmallScreen ? 12 : labelMaxChars;
                        return s.length > maxChars ? s.slice(0, maxChars) + "…" : s;
                    },
                },
            },

            dataZoom:
                data.length < 20
                    ? []
                    : [
                        {
                            type: "slider",
                            yAxisIndex: 0,
                            start: zoom.start,
                            end: zoom.end,
                            right: 4,
                            width: 12,
                            showDetail: false,
                        },
                        {
                            type: "inside",
                            yAxisIndex: 0,
                            zoomOnMouseWheel: "ctrl",
                            moveOnMouseWheel: true,
                        },
                    ],

            series: [
                {
                    type: "bar",
                    data: seriesData,
                    barWidth: 14,
                    barCategoryGap: "45%",
                },
            ],
            replaceMerge: ["series", "yAxis", "dataZoom"],
        }),
        [categories, seriesData, zoom, labelStep, labelMaxChars, data.length, isSmallScreen]
    );

    const onEvents = {
        datazoom: (e) => {
            const b = e?.batch?.[0];
            if (!b) return;

            const inst = chartRef.current?.getEchartsInstance?.();
            inst?.dispatchAction?.({ type: "hideTip" });

            setZoom({ start: b.start, end: b.end });
        },
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    width: "100%",
                    height: 560,
                    background: "#fff",
                    border: "1px solid #222",
                    borderRadius: "20px",
                    mb: 4,
                    py: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    pb: 2,
                }}
            >
                {activeTitle === true && (
                    <Box sx={{ pl: { xs: "20px", md: "40px" }, mb: 0 }}>
                        <Typography variant="secTitle">{tittle}</Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        height: "100%",
                    }}
                >
                    <Box sx={{ width: "90%", aspectRatio: "1/1", height: "100%" }}>
                        <ReactECharts
                            ref={chartRef}
                            option={option}
                            style={{ width: "100%", height: "100%" }}
                            onEvents={onEvents}
                            notMerge={false}
                            lazyUpdate={true}
                        />
                    </Box>
                </Box>
            </Box>

            {customIsolates.active === true ?

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "end",
                        pl: { xs: "20px", md: "40px" },
                    }}
                >
                    <Box
                        sx={{
                            width: "270px",
                            borderTop: "1px solid #222",
                            borderBottom: "1px solid #222",
                            textAlign: "end",
                            py: "10px",
                        }}
                    >
                        <Typography variant="subText">{customIsolates.value} isolates total</Typography>
                    </Box>
                </Box>

                :

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "end",
                        pl: { xs: "20px", md: "40px" },
                    }}
                >
                    <Box
                        sx={{
                            width: "270px",
                            borderTop: "1px solid #222",
                            borderBottom: "1px solid #222",
                            textAlign: "end",
                            py: "10px",
                        }}
                    >
                        <Typography variant="subText">{isolates} isolates total</Typography>
                    </Box>
                </Box>}
        </Box>
    );
}